/**
 * 30/60/90 Day Plan Phase Breakdown
 *
 * Subdivides roadmap quick-wins into 30/60/90-day sub-phases based on
 * effort level, priority ranking, and logical sequencing.
 * Does NOT modify roadmapGenerator.js.
 */

const EFFORT_PRIORITY_TO_SUBPHASE = {
  'Low-Critical': 'thirtyDay',
  'Low-High': 'thirtyDay',
  'Low-Medium': 'thirtyDay',
  'Low-Low': 'sixtyDay',
  'Medium-Critical': 'thirtyDay',
  'Medium-High': 'thirtyDay',
  'Medium-Medium': 'sixtyDay',
  'Medium-Low': 'ninetyDay',
  'High-Critical': 'sixtyDay',
  'High-High': 'sixtyDay',
  'High-Medium': 'ninetyDay',
  'High-Low': 'ninetyDay'
}

const INITIATIVE_DEPENDENCIES = {
  'identity-governance': ['mfa-implementation'],
  'dlp-implementation': ['data-classification'],
  'tabletop-exercises': ['ir-plan'],
  'grc-platform': ['metrics-dashboard'],
  'zero-trust': ['endpoint-protection', 'mfa-implementation'],
  'security-champions': ['security-awareness'],
  'risk-management': ['risk-register'],
  'compliance-tracking': ['policy-review']
}

const OWNER_SUGGESTIONS = {
  'advisor-access': {
    default: 'Client IT team',
    governance: 'Client leadership with advisor guidance',
    technical: 'Client IT team or MSP'
  },
  'strategic-partner': {
    default: 'Client IT team with advisor oversight',
    governance: 'Advisor-led, client-approved',
    technical: 'Client IT team with advisor guidance'
  },
  'integrated-ciso': {
    default: 'Advisor-led with client resources',
    governance: 'Advisor-led',
    technical: 'Advisor-directed, client or MSP executed'
  }
}

/**
 * Classify an initiative's owner category based on its domain.
 */
function classifyOwnerCategory(initiative) {
  const name = (initiative.name || initiative.title || '').toLowerCase()
  const governanceKeywords = ['governance', 'policy', 'committee', 'charter', 'framework', 'risk register', 'reporting', 'metrics']
  const technicalKeywords = ['endpoint', 'firewall', 'backup', 'mfa', 'siem', 'patch', 'vulnerability', 'scan', 'edr', 'network']

  if (governanceKeywords.some(k => name.includes(k))) return 'governance'
  if (technicalKeywords.some(k => name.includes(k))) return 'technical'
  return 'default'
}

/**
 * Get the initiative key for dependency checking.
 */
function getInitiativeKey(initiative) {
  return (initiative.id || initiative.name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

/**
 * Break down the quick-wins phase into 30/60/90-day sub-phases.
 *
 * @param {Object} roadmap - From generateRoadmap()
 * @param {string} serviceTier - 'advisor-access' | 'strategic-partner' | 'integrated-ciso'
 * @returns {Object} Enhanced phase breakdown
 */
export function generatePlanBreakdown(roadmap, serviceTier = 'strategic-partner') {
  if (!roadmap?.phases) return { thirtyDay: emptyPhase('First 30 days', '0-30 days'), sixtyDay: emptyPhase('31-60 days', '31-60 days'), ninetyDay: emptyPhase('61-90 days', '61-90 days'), strategic: emptyPhase('Strategic initiatives', '90-180 days'), longTerm: emptyPhase('Long-term transformation', '180-365 days'), totals: { quickWinCount: 0, thirtyDayCount: 0, sixtyDayCount: 0, ninetyDayCount: 0, strategicCount: 0, longTermCount: 0, totalInitiatives: 0 } }

  const ownerConfig = OWNER_SUGGESTIONS[serviceTier] || OWNER_SUGGESTIONS['strategic-partner']

  // Find the quick-wins phase
  const quickWinsPhase = roadmap.phases.find(p => p.id === 'quick-wins' || p.label?.toLowerCase().includes('quick'))
  const strategicPhase = roadmap.phases.find(p => p.id === 'strategic-investment' || p.label?.toLowerCase().includes('strategic'))
  const longTermPhase = roadmap.phases.find(p => p.id === 'long-term' || p.label?.toLowerCase().includes('long'))

  const quickWins = quickWinsPhase?.initiatives || []

  // Initial classification
  const classified = {
    thirtyDay: [],
    sixtyDay: [],
    ninetyDay: []
  }

  quickWins.forEach(initiative => {
    const effort = initiative.effort || 'Medium'
    const priority = initiative.priorityLevel || initiative.priority || 'Medium'
    const key = `${effort}-${priority}`
    const subPhase = EFFORT_PRIORITY_TO_SUBPHASE[key] || 'sixtyDay'
    const ownerCategory = classifyOwnerCategory(initiative)

    classified[subPhase].push({
      ...initiative,
      suggestedOwner: ownerConfig[ownerCategory],
      ownerCategory,
      sequenceNote: null,
      subPhaseReason: `${effort} effort, ${priority} priority`
    })
  })

  // Apply dependency sequencing
  const allClassified = [...classified.thirtyDay, ...classified.sixtyDay, ...classified.ninetyDay]
  for (const [dependent, prerequisites] of Object.entries(INITIATIVE_DEPENDENCIES)) {
    const depInit = allClassified.find(i => getInitiativeKey(i).includes(dependent))
    if (!depInit) continue

    for (const prereq of prerequisites) {
      const prereqInit = allClassified.find(i => getInitiativeKey(i).includes(prereq))
      if (!prereqInit) continue

      // Find which sub-phases they're in
      const depPhase = Object.entries(classified).find(([, items]) => items.includes(depInit))
      const prereqPhase = Object.entries(classified).find(([, items]) => items.includes(prereqInit))

      if (depPhase && prereqPhase) {
        const phaseOrder = { thirtyDay: 0, sixtyDay: 1, ninetyDay: 2 }
        if (phaseOrder[depPhase[0]] < phaseOrder[prereqPhase[0]]) {
          // Dependent is scheduled before prerequisite — bump it
          classified[depPhase[0]] = classified[depPhase[0]].filter(i => i !== depInit)
          depInit.sequenceNote = `Requires ${prereqInit.name || prereqInit.title} first`
          classified[prereqPhase[0]].push(depInit)
        }
      }
    }
  }

  // Build cost summaries
  const calcCosts = (items) => {
    const costs = items.reduce((sum, i) => {
      const cost = i.estimatedCost || i.cost || 0
      return { min: sum.min + (typeof cost === 'object' ? cost.min || 0 : cost * 0.7), max: sum.max + (typeof cost === 'object' ? cost.max || 0 : cost * 1.3) }
    }, { min: 0, max: 0 })
    return { min: Math.round(costs.min), max: Math.round(costs.max) }
  }

  const thirtyDay = {
    label: 'First 30 days',
    timeline: '0-30 days',
    initiatives: classified.thirtyDay,
    totalCost: calcCosts(classified.thirtyDay),
    summary: `${classified.thirtyDay.length} initiative${classified.thirtyDay.length !== 1 ? 's' : ''} focused on establishing foundation and immediate risk reduction`
  }

  const sixtyDay = {
    label: '31-60 days',
    timeline: '31-60 days',
    initiatives: classified.sixtyDay,
    totalCost: calcCosts(classified.sixtyDay),
    summary: `${classified.sixtyDay.length} initiative${classified.sixtyDay.length !== 1 ? 's' : ''} building on initial foundation with structured controls`
  }

  const ninetyDay = {
    label: '61-90 days',
    timeline: '61-90 days',
    initiatives: classified.ninetyDay,
    totalCost: calcCosts(classified.ninetyDay),
    summary: `${classified.ninetyDay.length} initiative${classified.ninetyDay.length !== 1 ? 's' : ''} advancing program maturity and operational capability`
  }

  const strategic = {
    label: 'Strategic initiatives',
    timeline: '90-180 days',
    initiatives: (strategicPhase?.initiatives || []).map(i => ({
      ...i,
      suggestedOwner: ownerConfig[classifyOwnerCategory(i)],
      ownerCategory: classifyOwnerCategory(i)
    })),
    totalCost: calcCosts(strategicPhase?.initiatives || [])
  }

  const longTerm = {
    label: 'Long-term transformation',
    timeline: '180-365 days',
    initiatives: (longTermPhase?.initiatives || []).map(i => ({
      ...i,
      suggestedOwner: ownerConfig[classifyOwnerCategory(i)],
      ownerCategory: classifyOwnerCategory(i)
    })),
    totalCost: calcCosts(longTermPhase?.initiatives || [])
  }

  return {
    thirtyDay,
    sixtyDay,
    ninetyDay,
    strategic,
    longTerm,
    totals: {
      quickWinCount: quickWins.length,
      thirtyDayCount: classified.thirtyDay.length,
      sixtyDayCount: classified.sixtyDay.length,
      ninetyDayCount: classified.ninetyDay.length,
      strategicCount: strategic.initiatives.length,
      longTermCount: longTerm.initiatives.length,
      totalInitiatives: quickWins.length + strategic.initiatives.length + longTerm.initiatives.length
    }
  }
}

function emptyPhase(label, timeline) {
  return { label, timeline, initiatives: [], totalCost: { min: 0, max: 0 }, summary: 'No initiatives in this phase' }
}
